<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { computed, h, nextTick, ref, watch } from 'vue'
import { twMerge } from 'tailwind-merge'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useAuth } from '@/composables/auth'
import { useMinesweeperChallengeLeaderboard } from '@/composables/game/leader-board'
import { useMinesweeperChallenge } from '@/composables/game/challenges'
import { usePlayer } from '@/composables/game/player'
import { SPECIAL_CURRENCY_ID } from '@/types/shared/player'
import { type CardChallengeLeaderboardPlayer, ChallengeMedal } from '@/types/leaderboard-player'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import { formatMillisecondDuration } from '@/helpers/date'
import ChallengeBoardCard2 from '@/components/ChallengeBoardCard2.vue'
import ChallengePrizeItem from '@/components/icons/ChallengePrizeItem.vue'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { isPrizePoolNft, MINI_GAME_ID } from '@/types/shared/player-config'
import GemImageWebp from '@/assets/images/special-currencies/gem.webp'
import ClockIcon from '@/assets/images/gold-clock.png'
import ClockSvgIcon from '@/assets/images/clock-icon.svg'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import MinesweeperChallengeIcon from '@/assets/images/challenges/minesweeper/minesweeper-challenge-icon.webp'
import api from '@/services/api'
import { useToaster } from '@/composables/game/toasters'
import { useRouter } from 'vue-router'
import { RouteName } from '@/routes'
import { useOutOfSpecialCurrency, usePathwayEventOverlay, usePathwayEvents } from '@/composables/game/pathway-events'
import ArrowRight from '@/components/icons/ArrowRight.vue'
import OutOfSpecialCurrencyModal from '@/components/modals/OutOfSpecialCurrencyModal.vue'
import SummaryCard from '@/components/SummaryCard.vue'
import { useMinesweeperChallengeModal } from '@/composables/game/minesweeper-challenge-modal'
import MinesweeperChallengeRulesModal from '@/components/modals/MinesweeperChallengeRulesModal.vue'
import { specialCurrenciesConfig } from '@/config/special-currencies'

const props = defineProps<{
	show: boolean
}>()
const emit = defineEmits<{
	hide: []
	play: [nonce: string]
}>()

const { profile } = useAuth()
const { player, updatePlayer } = usePlayer()
const { addHapticFeedback } = useHapticFeedback()
const { open } = useTonConnectModal()
const { rawAddress } = useWalletConnect()
const {
  getMinesweeperChallengeLeaderboard,
  minesweeperChallengeLeaderboard: players,
  currentPlayer,
} = useMinesweeperChallengeLeaderboard()

const {
  formattedCountdown,
  winnersCount,
  prizePool,
  getPrizeNameForPosition,
  getPrizeImageForPosition,
  getPrizeForPosition,
  getPrizeValueForPosition,
  prizeTypeImage,
  prizeTypeText,
  entryFee,
  entryCurrency,
  getBadgeForPosition,
} = useMinesweeperChallenge()
const { topNumber } = useFullScreenDimensions()
const { notify } = useToaster()
const router = useRouter()
const { pathwayEvents, anyActivePathwayEvent } = usePathwayEvents()
const { showFirstPathway } = usePathwayEventOverlay()
const { isReturningFromGame } = useMinesweeperChallengeModal()
const {
  selectedSpecialCurrencyForModal,
  canShowOutOfSpecialCurrencyModal,
  getAvailableEcommerceItems,
  closeOutOfSpecialCurrencyModal,
} = useOutOfSpecialCurrency()

const loading = ref(false)
const buyingEntry = ref(false)
const showPrizes = ref(false)
const shouldAnimateUser = ref(false)
const showRulesModal = ref(false)
const scrollContainer = ref<HTMLElement | null>(null)
const inScrollView = ref(false)
const isScrolled = ref(false)
const maxVisibleRank = ref<number>(100)
const listLimit = ref<number>(100)

const currencyImage = computed(() => {
  const currencyMap: Record<string, string> = {
    [SPECIAL_CURRENCY_ID.GEM]: specialCurrenciesConfig.gem.image,
    [SPECIAL_CURRENCY_ID.CROWN]: specialCurrenciesConfig.crown.image,
    [SPECIAL_CURRENCY_ID.GOLDEN_EGG]: specialCurrenciesConfig.goldenEgg.image,
    [SPECIAL_CURRENCY_ID.KEY]: specialCurrenciesConfig.key.image,
    [SPECIAL_CURRENCY_ID.PARACHUTE]: specialCurrenciesConfig.parachute.image,
    [SPECIAL_CURRENCY_ID.PLANT]: specialCurrenciesConfig.plant.image,
    [SPECIAL_CURRENCY_ID.TOP_HAT]: specialCurrenciesConfig.topHat.image,
    [SPECIAL_CURRENCY_ID.GOLD_FEATHER]: specialCurrenciesConfig.goldFeather.image2,
  }
  return currencyMap[entryCurrency.value] || GemImageWebp
})

const playerForCard = computed<CardChallengeLeaderboardPlayer | undefined>(() => {
  if (currentPlayer.value) {
    return {
      firstName: profile.value.firstName,
      photoUrl: profile.value.photoUrl,
      score: currentPlayer.value.score,
      isThisPlayer: true,
    }
  }
  return undefined
})

function handleScroll() {
  if (scrollContainer.value) {
    if (currentPlayer.value?.rank) {
      const cardElement = scrollContainer.value.querySelector(`.card:nth-child(${currentPlayer.value.rank})`) as HTMLElement
      if (cardElement) {
        const cardRect = cardElement.getBoundingClientRect()
        inScrollView.value = cardRect.bottom <= scrollContainer.value.getBoundingClientRect().bottom
      } else {
        inScrollView.value = false
      }
    }
    isScrolled.value = scrollContainer.value.scrollTop > 30
  }
}

function getCardMedal(rank: number): ChallengeMedal | undefined {
  switch (rank) {
  case 1: {
    return ChallengeMedal.GOLD
  }
  case 2: {
    return ChallengeMedal.SILVER
  }
  case 3: {
    return ChallengeMedal.BRONZE
  }
  default: {
    return undefined
  }
  }
}

async function onCtaClick() {
  if (!rawAddress.value) {
    open()
    return
  }

  // Check if player has enough of the required currency
  const playerCurrency = player.value.specialCurrencies[entryCurrency.value] || 0
  if (playerCurrency < entryFee.value) {
    if (canShowOutOfSpecialCurrencyModal(entryCurrency.value)) {
      selectedSpecialCurrencyForModal.value = entryCurrency.value
      return
    }
    notify({
      type: 'error',
      title: `<span class="flex items-center gap-1.5">Not enough <img width="14px" height="14px" style="display: inline-block; vertical-align: middle;" src="${currencyImage.value}" alt="Icon"></span>`,
      description: h('span', { class: 'font-medium' }, [
        h('span', {
          class: 'underline text-green-primary cursor-pointer',
          onClick: () => {
            if (anyActivePathwayEvent.value) {
              showFirstPathway(pathwayEvents.value)
            } else {
              router.push({ name: RouteName.HOME })
            }
            emit('hide')
          },
        }, 'Explore pathways'),
        ' to get more now!',
      ]),
    })
    return
  }

  buyingEntry.value = true
  try {
    const { playerStats, nonce, error } = await api.buyMiniGameEntry({ gameId: MINI_GAME_ID.MINESWEEPER })

    if (error?.includes('another_entry_in_progress')) {
      notify({
        type: 'error',
        title: 'Purchase Failed',
        description: 'Please wait 1 minute for the next play',
      })
      return
    }

    if (!nonce) {
      throw new Error('No nonce received from server')
    }
    updatePlayer(playerStats)
    const url = `/${MINI_GAME_ID.MINESWEEPER}?nonce=${nonce}`
    window.history.replaceState({}, '', url)
    window.location.reload()
  } catch (error) {
    console.error('Failed to buy minesweeper game entry:', error)
    notify({
      type: 'error',
      title: 'Purchase Failed',
      description: 'Failed to buy minesweeper game entry',
    })
  } finally {
    setTimeout(() => buyingEntry.value = false, 500)
  }
}

function scrollToCurrentUser() {
  if (!currentPlayer.value?.rank || !scrollContainer.value) return

  // Check if user's rank is within visible range
  if (currentPlayer.value.rank > maxVisibleRank.value) return

  // Store the rank before nextTick to avoid TypeScript issues
  const userRank = currentPlayer.value.rank

  nextTick(() => {
    // User's rank is 1-based, array index is 0-based
    const userIndex = userRank - 1
    const cards = scrollContainer.value?.querySelectorAll('.card')
    const userCardElement = cards?.[userIndex] as HTMLElement

    if (userCardElement) {
      userCardElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

      // Enable animation after a short delay
      setTimeout(() => {
        shouldAnimateUser.value = true

        // Disable animation after 5 seconds
        setTimeout(() => {
          shouldAnimateUser.value = false
        }, 5000)
      }, 500)
    }
  })
}

watch(() => props.show, async () => {
  if (props.show) {
    loading.value = true
    await getMinesweeperChallengeLeaderboard()
    loading.value = false

    if (isReturningFromGame.value) {
      setTimeout(() => {
        scrollToCurrentUser()
      }, 100)
    }
  } else {
    // Reset animation when modal closes
    shouldAnimateUser.value = false
  }
}, { immediate: true })

watch(rawAddress, async rawAddress => {
  if (!rawAddress) return
  loading.value = true
  await getMinesweeperChallengeLeaderboard()
  loading.value = false
})

watch([scrollContainer, currentPlayer, players], () => {
  inScrollView.value = true
  nextTick(() => {
    setTimeout(() => {
      handleScroll()
    }, 50)
  })
}, { immediate: true })
</script>

<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="dro-overlay fixed max-h-screen h-screen w-screen left-0 bottom-0 bg-dark-most-bottom-layer  backdrop-blur-[4px] flex flex-col z-[800] normal-zeros overflow-x-hidden"
    >
      <div
        ref="scrollContainer"
        class="h-full w-full flex-1 flex flex-col px-4 overflow-y-auto overflow-x-hidden"
        :style="{ paddingTop: `${topNumber + 24}px` }"
        @scroll="handleScroll"
      >
        <div class="flex items-center z-[1]">
          <Transition
            name="slide-fade"
            mode="out-in"
          >
            <div
              v-if="showPrizes"
              class="w-6 h-6 min-w-6 flex items-center justify-center top-6 left-6 z-[2] cursor-pointer mr-1.5"
              @click="showPrizes = false"
            >
              <ArrowRight class="w-full h-full rotate-180" />
            </div>
          </Transition>
          <!-- Currency Balance Pill -->
          <div class="flex-1 flex items-center gap-1 mr-4">
            <Transition
              name="pill-slide"
              mode="out-in"
            >
              <div
                :key="showPrizes ? 'with-arrow' : 'without-arrow'"
                class="balance-pill scale-in"
              >
                <div class="w-5 h-5 min-w-[20px] relative flex justify-center">
                  <img
                    class="object-cover w-auto h-5"
                    :src="currencyImage"
                    :alt="entryCurrency"
                  >
                </div>
                <span>{{ formatNumberFullTruncate(player.specialCurrencies[entryCurrency] ?? 0) }}</span>
              </div>
            </Transition>
          </div>
          <CrossCircleIcon
            class="size-8 min-w-[32px] text-white/[87%]"
            @click="$emit('hide')"
          />
        </div>
        <div class="flex flex-col w-full h-full items-center text-center relative">
          <div class="relative flex justify-center items-center">
            <div
              class="absolute size-[175px] scale-110 rounded-full bg-gradient-to-br from-[#B8852E] via-[#B8852E] to-black blur-[50px] opacity-90"
            />
            <img
              :src="MinesweeperChallengeIcon"
              class="relative size-[175px] pointer-events-none z-10"
              alt="Minesweeper Challenge"
            >
          </div>
          <h2 class="text-light-white text-[36px] leading-9 font-bold mb-2">
            <T
              v-if="showPrizes"
              key-name="minesweeper_challenge.modal.prizes_title"
              default-value="Prizes"
            />
            <template v-else>
              Minesweeper
            </template>
          </h2>
          <p
            class="font-medium text-base leading-5 -tracking-[0.32px] mb-6 whitespace-pre-line"
            :class="showPrizes ? 'text-light-white' : 'text-light-white/70'"
          >
            <template v-if="showPrizes">
              Prizes will be sent shortly after the end of the challenge. Make sure to
              <span
                :class="{'pointer-events-none': !!rawAddress, 'underline text-green-primary': !rawAddress}"
                @click="open"
              >
                <T
                  key-name="general.connect"
                  default-value="connect"
                />
              </span>
              your wallet to receive your prize!
            </template>

            <template v-else>
              Clear the grid without hitting mines as fast as you can and climb the leaderboard!
              <span v-if="!rawAddress">
                <span
                  :class="{'pointer-events-none': !!rawAddress, 'underline': !rawAddress}"
                  @click="open"
                >
                  <T
                    key-name="general.connect"
                    default-value="Connect"
                  />
                </span>
                your wallet to join.
              </span>
            </template>
          </p>

          <Transition
            name="content-fade"
            mode="out-in"
          >
            <div
              v-if="showPrizes && prizePool"
              key="prizes"
              class="w-full flex-1 flex flex-col pb-6"
            >
              <ChallengePrizeItem
                v-for="(prizeItem, ind) in prizePool.amounts"
                :key="ind"
                :prize-type="prizePool.type"
                :prize-item="prizeItem"
                :prize-image="prizeTypeImage"
              />
            </div>

            <div
              v-else
              key="main"
              class="flex-1 w-full flex flex-col items-center"
            >
              <div
                class="mb-6 -mx-6 h-7 w-[calc(100%+48px)] bg-red-green-orange-countdown-banner flex items-center justify-center"
              >
                <div class="uppercase text-white text-base font-bold text-shadow-default pr-1.5">
                  Ends in
                </div>
                <img
                  class="size-[18px] object-cover"
                  :src="ClockIcon"
                  alt="Clock"
                >
                <div class="ml-1 golden-text-black-stroke text-base font-bold gold-text-shadow px-0.5">
                  {{ formattedCountdown }}
                </div>
              </div>
              <BaseButton
                class="mb-4 w-full"
                button-class="border-0 h-full py-4 px-6 h-[60px]"
                :disabled="buyingEntry"
                @click="onCtaClick"
              >
                <div class="flex items-center justify-center button-text">
                  <template v-if="!rawAddress">
                    <T
                      key-name="wallet.connect_wallet"
                      default-value="Connect wallet"
                    />
                  </template>
                  <template v-else-if="buyingEntry">
                    <span class="text-lg font-bold">PROCESSING...</span>
                  </template>
                  <template v-else>
                    <span class="text-lg font-bold tracking-[1px]">PLAY FOR</span>
                    <img
                      :src="currencyImage"
                      class="size-5 mx-1.5"
                      :alt="entryCurrency"
                    >
                    <span class="text-lg font-bold">{{ entryFee }}</span>
                  </template>
                </div>
              </BaseButton>

              <div class="w-full flex gap-x-4">
                <SummaryCard
                  content-class="pt-3"
                  top-text="Total Prizes"
                  :middle-image="prizeTypeImage"
                  :middle-text="prizeTypeText"
                  :bottom-text="`${winnersCount} WINNERS`"
                  @click="showPrizes = true"
                />
                <SummaryCard
                  background-class="w-1/2"
                  border-class="bg-white/[.38]"
                  top-text="Rule"
                  top-class="text-base font-semibold leading-[24px] text-light-white"
                  middle-text="Play"
                  middle-class="whitespace-pre-line text-center text-xl font-bold leading-6 text-indiana-dark"
                  bottom-class="whitespace-pre-line text-center text-xl font-bold leading-6 text-indiana-dark"
                  bottom-text="Minesweeper"
                  @click="showRulesModal = true"
                />
              </div>

              <div
                v-if="!loading"
                :class="twMerge('w-full flex flex-col relative', !players.length && 'flex-1 items-center justify-center  h-full')"
              >
                <div
                  v-if="players.length"
                  class="mt-6 flex flex-col w-full gap-y-2 pb-4 items-center"
                >
                  <ChallengeBoardCard2
                    v-for="(leadingBoardPlayer, index) in players.slice(0, maxVisibleRank)"
                    :key="index"
                    :ref="leadingBoardPlayer.isThisPlayer ? 'userCard' : undefined"
                    :player="leadingBoardPlayer"
                    :index="(index+1).toString()"
                    :is-own="leadingBoardPlayer.isThisPlayer"
                    :medal="getCardMedal(index+1)"
                    :prize-value="getPrizeValueForPosition(index+1)"
                    :prize-image="getPrizeImageForPosition(index+1)"
                    :prize-name="getPrizeNameForPosition(index+1)"
                    :score-image="ClockSvgIcon"
                    :score-value="formatMillisecondDuration(leadingBoardPlayer.score || 0)"
                    :is-prize-pool-nft="isPrizePoolNft(getPrizeForPosition(index+1))"
                    :badge="getBadgeForPosition(index+1)"
                    :class="['card', shouldAnimateUser && leadingBoardPlayer.isThisPlayer ? 'pulse-shadow' : '']"
                  />
                  <p
                    v-if="listLimit <= players.length"
                    class="mt-3 mb-4 text-center max-w-[314px] text-base font-medium text-white/60"
                    :class="currentPlayer && currentPlayer.rank > players.length && 'pb-10'"
                  >
                    And many more players who didn't make it into the top {{ Math.min(players.length, listLimit) }}...
                    yet.
                  </p>
                  <ChallengeBoardCard2
                    v-else-if="players.length > 1"
                    :player="playerForCard"
                    :index="`${(maxVisibleRank).toString()}+`"
                    :prize-value="getPrizeValueForPosition(maxVisibleRank)"
                    :prize-image="getPrizeImageForPosition(maxVisibleRank)"
                    :prize-name="getPrizeNameForPosition(maxVisibleRank)"
                    :score-image="ClockSvgIcon"
                    :score-value="playerForCard ? formatMillisecondDuration(playerForCard.score || 0) : ''"
                    :is-prize-pool-nft="isPrizePoolNft(getPrizeForPosition(maxVisibleRank+1))"
                    :badge="getBadgeForPosition(maxVisibleRank+1)"
                    card-class="invisible"
                  />
                </div>
                <div
                  v-else
                  class="flex flex-1 flex-col items-center justify-center w-full text-center py-6"
                >
                  <h2 class="text-lg -tracking-[0.36px] font-bold text-light-white">
                    Nothing to see here yet
                  </h2>
                  <p class="text-sm font-medium tracking-tight text-white/[.87]">
                    Play the minesweeper game and be the first to join the challenge
                  </p>
                </div>
                <div
                  v-if="playerForCard && currentPlayer && !inScrollView"
                  class="w-full fixed bottom-4 z-[900] left-1/2 -translate-x-1/2 px-4"
                >
                  <ChallengeBoardCard2
                    :player="playerForCard"
                    :index="formatUnshortenedNumber(currentPlayer.rank)"
                    :medal="getCardMedal(currentPlayer.rank)"
                    :prize-value="getPrizeValueForPosition(currentPlayer.rank)"
                    :prize-image="getPrizeImageForPosition(currentPlayer.rank)"
                    :prize-name="getPrizeNameForPosition(currentPlayer.rank)"
                    :score-image="ClockSvgIcon"
                    :score-value="playerForCard ? formatMillisecondDuration(playerForCard.score || 0) : ''"
                    :is-prize-pool-nft="isPrizePoolNft(getPrizeForPosition(currentPlayer.rank))"
                    :badge="getBadgeForPosition(currentPlayer.rank)"
                    is-own
                    card-class="border-green-primary"
                    rank-wrapper-class="min-w-fit mr-1"
                  />
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
      <MinesweeperChallengeRulesModal
        :show="showRulesModal"
        @hide="showRulesModal = false"
      />
      <OutOfSpecialCurrencyModal
        v-if="selectedSpecialCurrencyForModal"
        :show="!!selectedSpecialCurrencyForModal"
        :currency="selectedSpecialCurrencyForModal"
        :ecommerce-items="getAvailableEcommerceItems(selectedSpecialCurrencyForModal)"
        subtitle="Get more and keep exploring!"
        :has-checkmark="false"
        @hide="closeOutOfSpecialCurrencyModal($event)"
      />
    </div>
  </Transition>
</template>

<style scoped lang="scss">

.balance-pill {
	@apply py-1.5 pl-2.5 pr-3 rounded-full bg-dark-top-most-layer flex items-center gap-x-1.5 h-8 text-sm font-semibold -tracking-[0.28px] min-w-max;
	position: relative;
	transition: all 0.3s ease;
}

.scale-in {
	animation: scaleIn 0.3s ease-out;
}

@keyframes scaleIn {
	from {
		transform: scale(0.9);
		opacity: 0;
	}
	to {
		transform: scale(1);
		opacity: 1;
	}
}

.bg-red-green-orange-countdown-banner {
	background: linear-gradient(90deg, #B8852E 0%, #950A0A 30.35%, #B8852E 80.77%);
	box-shadow: 0px 8px 12px rgba(0, 0, 0, 0.52);
}

.scrollable-container {
	-ms-overflow-style: none; /* Internet Explorer 10+ */
	scrollbar-width: none; /* Firefox */
}

.scrollable-container::-webkit-scrollbar {
	display: none; /* Safari and Chrome */
}

.yellow-countdown-banner {
	background: linear-gradient(90deg, #FFD700 0%, #FFA500 100%);
}

.text-shadow-default {
	text-shadow: 0px 0px 115.874px rgba(0, 0, 0, 0.50);
}

.button-text {
	text-shadow: 0 0 56px rgba(0, 0, 0, 0.50);
}

.pulse-shadow {
	animation: pulseShadow 1.5s ease-in-out infinite;
}

@keyframes pulseShadow {
	0%, 100% {
		box-shadow: 0 0 12px 0 #3B9A7C;
	}
	50% {
		box-shadow: 0 0 32px 0 #3B9A7C;
	}
}

// Slide-fade transition for arrow
.slide-fade-enter-active,
.slide-fade-leave-active {
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from {
	transform: translateX(-10px);
	opacity: 0;
}

.slide-fade-leave-to {
	transform: translateX(-10px);
	opacity: 0;
}

.content-fade-enter-active,
.content-fade-leave-active {
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.content-fade-enter-from {
	opacity: 0;
}

.content-fade-leave-to {
	opacity: 0;
}

</style>

<style lang="scss">
@import "@/assets/styles/challenges";
</style>